import { useEffect, useState, useCallback } from "react";
import { UserCog, Plus, KeyRound, ShieldCheck, Power, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY = { name: "", email: "", role: "assistant", password: "" };

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700", gold: "bg-gold/20 text-navy",
    green: "bg-emerald-100 text-emerald-700", red: "bg-red-100 text-red-700",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ roles: [], modules: [], actions: [], flags: [] });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited for permissions
  const [form, setForm] = useState(EMPTY);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/users/roles").then((r) => setMeta(r.data)).catch(() => {});
  }, [load]);

  const roleLabel = (key) => meta.roles.find((r) => r.key === key)?.label || key;

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setCreating(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "inactive" ? "active" : "inactive";
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status: next });
      toast.success(next === "active" ? "User activated" : "User deactivated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePermissions = async () => {
    try {
      await api.put(`/admin/users/${editing.id}`, {
        role: editing.role, permissions: editing.permissions, flags: editing.flags, name: editing.name,
      });
      toast.success("Permissions updated");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const applyRoleTemplate = (roleKey) => {
    const tpl = meta.roles.find((r) => r.key === roleKey);
    if (!tpl) return;
    setEditing((prev) => ({ ...prev, role: roleKey, permissions: JSON.parse(JSON.stringify(tpl.permissions)), flags: { ...tpl.flags } }));
  };

  const togglePerm = (mod, act) => {
    setEditing((prev) => {
      const perms = JSON.parse(JSON.stringify(prev.permissions || {}));
      const cur = perms[mod] || [];
      perms[mod] = cur.includes(act) ? cur.filter((a) => a !== act) : [...cur, act];
      return { ...prev, permissions: perms };
    });
  };

  const toggleFlag = (flag) => {
    setEditing((prev) => ({ ...prev, flags: { ...(prev.flags || {}), [flag]: !(prev.flags || {})[flag] } }));
  };

  const setPassword = async () => {
    try {
      await api.post(`/admin/users/${pwUser.id}/reset-password`, { password: newPw });
      toast.success("Password updated");
      setPwUser(null); setNewPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-manager-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserCog className="text-gold" size={24} />
          <h1 className="font-serif text-3xl font-bold text-navy">Users &amp; Roles</h1>
        </div>
        <button data-testid="add-user-btn" onClick={() => { setForm(EMPTY); setCreating(true); }}
          className="btn-gold inline-flex items-center gap-2"><Plus size={16} /> Add user</button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Individual staff accounts with role-based permissions enforced across the system. Deactivating a user preserves their history.</p>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id || u.email} data-testid="user-row" className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{u.name}{u.is_demo && <span className="ml-2 text-xs text-slate-400">demo</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><Badge tone={u.is_super ? "gold" : "slate"}>{roleLabel(u.role)}</Badge></td>
                <td className="px-4 py-3"><Badge tone={u.status === "inactive" ? "red" : "green"}>{u.status || "active"}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button title="Permissions" data-testid={`edit-perms-${u.email}`}
                      onClick={() => setEditing({ ...u, permissions: u.permissions || {}, flags: u.flags || {} })}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-navy"><ShieldCheck size={16} /></button>
                    <button title="Reset password" data-testid={`reset-pw-${u.email}`}
                      onClick={() => { setPwUser(u); setNewPw(""); }}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-navy"><KeyRound size={16} /></button>
                    {!u.is_super && (
                      <button title={u.status === "inactive" ? "Activate" : "Deactivate"} data-testid={`toggle-status-${u.email}`}
                        onClick={() => toggleStatus(u)}
                        className={`rounded-md p-1.5 hover:bg-slate-100 ${u.status === "inactive" ? "text-emerald-600" : "text-red-500"}`}><Power size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      {creating && (
        <Overlay onClose={() => setCreating(false)} title="Add user">
          <form onSubmit={submitCreate} className="space-y-4">
            <div><Label>Full name</Label><Input data-testid="user-name-input" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><Label>Email</Label><Input data-testid="user-email-input" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div>
              <Label>Role</Label>
              <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
                {meta.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div><Label>Temporary password</Label><Input data-testid="user-password-input" type="text" className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} /></div>
            <button type="submit" data-testid="create-user-submit" className="btn-gold w-full">Create user</button>
          </form>
        </Overlay>
      )}

      {/* Permissions editor */}
      {editing && (
        <Overlay onClose={() => setEditing(null)} title={`Permissions — ${editing.name}`} wide>
          {editing.is_super ? (
            <p className="rounded-lg bg-gold/10 p-4 text-sm text-navy">This user is a Director / Super Admin and has full access to every module. To limit access, change their role first.</p>
          ) : (
            <>
              <div className="mb-4">
                <Label>Role template</Label>
                <select data-testid="edit-role-select" value={editing.role} onChange={(e) => applyRoleTemplate(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy">
                  {meta.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                <p className="mt-1 text-xs text-slate-400">Changing the role resets permissions to its template. Fine-tune below.</p>
              </div>

              <div className="mb-4 rounded-lg border border-slate-200 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Special access</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {meta.flags.map((f) => (
                    <label key={f} className="flex items-center gap-2 text-sm text-navy">
                      <Switch data-testid={`flag-${f}`} checked={!!(editing.flags || {})[f]} onCheckedChange={() => toggleFlag(f)} />
                      {f.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr><th className="px-3 py-2">Module</th>{meta.actions.map((a) => <th key={a} className="px-2 py-2 text-center capitalize">{a}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {meta.modules.map((mod) => (
                      <tr key={mod}>
                        <td className="px-3 py-2 font-medium capitalize text-navy">{mod}</td>
                        {meta.actions.map((act) => (
                          <td key={act} className="px-2 py-2 text-center">
                            <input type="checkbox" data-testid={`perm-${mod}-${act}`}
                              checked={((editing.permissions || {})[mod] || []).includes(act)}
                              onChange={() => togglePerm(mod, act)}
                              className="h-4 w-4 accent-[#B8893B]" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={savePermissions} data-testid="save-perms-btn" className="btn-gold mt-4 w-full">Save permissions</button>
            </>
          )}
        </Overlay>
      )}

      {/* Reset password */}
      {pwUser && (
        <Overlay onClose={() => setPwUser(null)} title={`Reset password — ${pwUser.name}`}>
          <Label>New password</Label>
          <Input data-testid="new-password-input" type="text" className="mt-1.5" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} />
          <button onClick={setPassword} data-testid="save-password-btn" disabled={newPw.length < 8} className="btn-gold mt-4 w-full disabled:opacity-60">Update password</button>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 w-full ${wide ? "max-w-3xl" : "max-w-md"} rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-navy">{title}</h2>
          <button onClick={onClose} data-testid="overlay-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
